import "./App.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import "./assets/css/style.css";

import {
  finalMissedRoute,
  todayMissedRoute,
  todayPendingRoute,
} from "./storage";
import { authOTPVerified, authToken } from "./storage/authToken";

import {
  useCustomDispatch,
  useCustomLocation,
  useCustomNavigate,
  useDriverLiveActivity,
  useVersionCheck,
} from "./hooks"; 

import { useAuthSelector } from "./services/selectors/authSelector";
import DoseGapOverrideModal from "./components/common/DoseGapOverrideModal";
import { storeFlexiMarkedNotification } from "./services/slices/supervisor/pendingCounselling";
import { persistor } from "./services/store/store"; 
import { markPatientPrescriptionsPharmacistSigned } from "./services/slices/dispense/pharmacistPrescriptionSignature";
import {
  primeVideoCallRealtimeConnection,
  publishVideoCallEnded,
  subscribeToDeliveryVideoCallNotifications,
  subscribeToVideoCallSession,
} from "./components/video-calling/videoCallHelper";
import { endDriverPharmacistDeliveryVideoCall } from "./components/video-calling/deliveryVideoCallApi";
import {
  setupPushNotifications,
  requestNotificationPermission,
} from "./components/video-calling/pushNotificationSetup";
import {
  isIgnorableAblyConnectionCloseError,
  isIOS,
  isIOSSafari,
  safeCloseAblyRealtime,
} from "./utils/helper/helper";
import { saveDeliveryCallImage } from "./services/slices/supervisor/videoCallInitiate";

import VideoCallRequestHandler from "./components/video-calling/VideoCallRequestHandler";
import PharmacistDriverIncomingCallHandler from "./components/video-calling/PharmacistDriverIncomingCallHandler";
import VideoCallErrorBoundary from "./components/video-calling/VideoCallErrorBoundary";
import VideoCall from "./components/video-calling/VideoCall";
import { HandoverSignaturePad } from "./components/common/handoverSignature";
import AnimatedBottomSheet from "./components/common/animatedBottomSheet";

import AppRoutes from "./routes/appRoutes";
import Cookies from "js-cookie";

import * as routeNames from "./routes/routeName";
import * as EndPoints from "./networking/urlEndPoints";
import { getAblyRealtime } from "./networking/ablyClient";
import { subscribeToDriverHandoverSocketUpdates } from "./services/realtime/realtimeSocket";
import {
  getStoredToken,
  isFirebaseMessagingAvailable,
  onMessageListener,
  requestForToken,
} from "./firebase";
import { saveFcmTokenToBackend } from "./services/apis/notifications/saveFcmToken";
import {
  acceptDriverHandoverRequest,
  denyDriverLiveLocationRequest,
  fetchDriverActiveRoutes,
  fetchDriverRouteInterruptionStatus,
  recordDriverActivitySignal,
  rejectDriverHandoverRequest,
  resumeDriverRouteInterruption,
  updateDriverLiveLocation,
} from "./services/apis/dispense/routeAudit";
import { clearLogoutBlockedMessage } from "./services/slices/pharmacy/logout";
import { reverseGeocode } from "./utils/helper/geocode";
import { isOwnDriverHandoverRequest } from "./utils/helper/handoverEvents";
import { APP_TIMEZONE } from "./utils/constants/timezone";

const apiBaseUrl = String(process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
const DRIVER_SESSION_EXPIRED_MESSAGE_KEY = "driver_session_expired_message";
const DRIVER_SESSION_EXPIRED_MESSAGE = "Your session expired. Please login again.";
const routeReleaseEventTypes = new Set([
  "route_timeout_expired",
  "route_ownership_released",
]);
const routeInterruptionDateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: APP_TIMEZONE,
  month: "short",
  day: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

const formatRouteInterruptionDateTime = (value, fallback = "-") => {
  if (!value) {
    return fallback;
  }

  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return fallback;
  }

  const parts = routeInterruptionDateFormatter.formatToParts(parsedDate);
  const getPart = (type) => parts.find((part) => part.type === type)?.value || "";

  const month = getPart("month");
  const day = getPart("day");
  const year = getPart("year");
  const hour = getPart("hour");
  const minute = getPart("minute");
  const dayPeriod = getPart("dayPeriod").toUpperCase();

  return `${month} ${day}, ${year} ${hour}:${minute} ${dayPeriod}`.trim();
};

// Delegates to the single app-wide Ably client (see networking/ablyClient.js) so the
// driver-app handlers share ONE connection with videoCallHelper instead of opening a
// second competing connection to pharmacy-notifications (the Android duplicate-connection
// bug that caused missed handover pop-ups).
const getSharedDriverAppRealtime = () => getAblyRealtime();

const clearStoredRouteState = () => {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(todayPendingRoute);
    localStorage.removeItem(todayMissedRoute);
    localStorage.removeItem(finalMissedRoute);
  }

  Cookies.set("td", false);
};

const requestCurrentPosition = () =>
  new Promise((resolve, reject) => {
    if (!navigator?.geolocation) {
      reject(new Error("Location services are not available on this device."));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 10000,
    });
  });

const normalizeLocationPermissionState = (state) => {
  if (state === "granted" || state === "prompt" || state === "denied") {
    return state;
  }

  return "unsupported";
};

const resolveLocationPermissionStateFromError = (error) => {
  if (error?.code === 1) {
    return "denied";
  }

  if (!navigator?.geolocation) {
    return "unsupported";
  }

  return "prompt";
};

const getLocationPermissionErrorMessage = (error) => {
  if (!navigator?.geolocation) {
    return typeof window !== "undefined" && !window.isSecureContext
      ? "Location access requires a secure connection. Please open the secure app link and try again."
      : "Location services are not available on this device.";
  }

  switch (error?.code) {
    case 1:
      return "Location is off. Turn it on in settings to unlock the app.";
    case 2:
      return "We could not read your location. Make sure location services are turned on, then try again.";
    case 3:
      return "The location request timed out. Please try again in an area with a stronger signal.";
    default:
      return error?.message || "Location access is required to continue using the app.";
  }
};

function App() {
  const navigate = useCustomNavigate();
  const dispatch = useCustomDispatch();
  const logoutBlockedMessage = useSelector((state) => state.driverLogout?.blockedMessage);
  const location = useCustomLocation();

  const {
    pharmacyId,
    authLoginResponse,
    driverID,
    isPharmacistForSelectedPharmacy,
    pharmacistIdForSelectedPharmacy,
    pharmacistNameForSelectedPharmacy,
  } = useAuthSelector();
  const driverName = `${authLoginResponse?.data?.data?.driver_profile?.name ?? ""}`
    .trim()
    .toLowerCase();
  const handoverRecipientTargets = [
    { type: "driver", id: driverID },
    isPharmacistForSelectedPharmacy && pharmacistIdForSelectedPharmacy != null
      ? { type: "pharmacy_user", id: pharmacistIdForSelectedPharmacy }
      : null,
  ].filter((target) => target?.id != null);

  const { updateAvailable, reloadPage } = useVersionCheck();
  // reloadPage() now purges caches before navigating, so it is async and takes a
  // moment. Guard against double-taps and show the driver it is working.
  const [isReloading, setIsReloading] = useState(false);
  const handleUpdateReload = async () => {
    if (isReloading) return;
    setIsReloading(true);
    try {
      await reloadPage();
    } catch (error) {
      console.error("Reload failed", error);
      setIsReloading(false);
    }
  };
  const isOtpVerified = Number(authOTPVerified()) === 1;
  const isOtpVerificationScreen = location.pathname === routeNames.verify_otp;
  const isSwitchPharmacyScreen = location.pathname === routeNames.SWITCH_PHARMACY;
  const showLogoutBlockedHandoverButton =
    !isOtpVerificationScreen && !isSwitchPharmacyScreen;
  const activityRoute = `${new URLSearchParams(location.search).get("route") ?? ""}`.trim();

  // State for pharmacist video call
  const [pharmacistVideoCall, setPharmacistVideoCall] = useState({
    isOpen: false,
    callData: null,
  });
  const pharmacistSessionAblyRef = useRef(null);
  const handledDriverHandoverRequestsRef = useRef(new Set());
  const handledPatientSpottedEventsRef = useRef(new Set());
  const dismissedRouteInterruptionIdRef = useRef(null);
  const routeInterruptionActiveRef = useRef(false);
  const locationPermissionStatusRef = useRef(null);

  const [handoverNotification, setHandoverNotification] = useState(null);
  const [routeReleasedNotice, setRouteReleasedNotice] = useState(null);
  const handoverNotificationTimerRef = useRef(null);

  const [incomingHandoverRequest, setIncomingHandoverRequest] = useState(null);
  const [receiverSignature, setReceiverSignature] = useState("");
  const [handoverRequestActionLoading, setHandoverRequestActionLoading] = useState(false);
  const [handoverRequestError, setHandoverRequestError] = useState(null);
  const [handoverRequestSuccess, setHandoverRequestSuccess] = useState(null);
  const [routeInterruption, setRouteInterruption] = useState(null);
  const [routeResumeExplanation, setRouteResumeExplanation] = useState("");
  const [routeResumeLoading, setRouteResumeLoading] = useState(false);
  const [routeResumeError, setRouteResumeError] = useState(null);
  const [patientSpottedNotification, setPatientSpottedNotification] = useState(null);
  const [locationPermissionState, setLocationPermissionState] = useState("checking");
  const [locationPermissionRequestLoading, setLocationPermissionRequestLoading] = useState(false);
  const [locationPermissionError, setLocationPermissionError] = useState("");
  const shouldEnforceLocationPermission =
    String(process.env.REACT_APP_ENFORCE_LOCATION_PERMISSION || "")
      .trim()
      .toLowerCase() === "true";
  const isLocationAccessGranted = locationPermissionState === "granted";
  const showLocationPermissionLock =
    shouldEnforceLocationPermission &&
    locationPermissionState !== "checking" &&
    !isLocationAccessGranted;
  const shouldUseDirectLocationProbe = isIOS();

  const refreshLocationPermissionState = useCallback(async ({ probeCurrentPosition = false } = {}) => {
    if (!navigator?.geolocation) {
      setLocationPermissionState("unsupported");
      setLocationPermissionError(getLocationPermissionErrorMessage());
      return "unsupported";
    }

    if (navigator?.permissions?.query && !shouldUseDirectLocationProbe) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
        if (
          locationPermissionStatusRef.current &&
          locationPermissionStatusRef.current !== permissionStatus
        ) {
          locationPermissionStatusRef.current.onchange = null;
        }

        locationPermissionStatusRef.current = permissionStatus;
        permissionStatus.onchange = () => {
          const nextState = normalizeLocationPermissionState(permissionStatus.state);
          setLocationPermissionState(nextState);
          setLocationPermissionError(
            nextState === "denied"
              ? getLocationPermissionErrorMessage({ code: 1 })
              : "",
          );
        };

        const nextState = normalizeLocationPermissionState(permissionStatus.state);
        setLocationPermissionState(nextState);
        setLocationPermissionError(
          nextState === "denied"
            ? getLocationPermissionErrorMessage({ code: 1 })
            : "",
        );

        if (nextState === "granted" || !probeCurrentPosition) {
          return nextState;
        }
      } catch (error) {
        void error;
      }
    }

    if (!probeCurrentPosition) {
      setLocationPermissionState((currentState) =>
        currentState === "granted" ? currentState : "prompt",
      );
      setLocationPermissionError("");
      return "prompt";
    }

    try {
      await requestCurrentPosition();
      setLocationPermissionState("granted");
      setLocationPermissionError("");
      return "granted";
    } catch (error) {
      const nextState = resolveLocationPermissionStateFromError(error);
      setLocationPermissionState(nextState);
      setLocationPermissionError(getLocationPermissionErrorMessage(error));
      return nextState;
    }
  }, [shouldUseDirectLocationProbe]);

  const handleRequestLocationPermission = useCallback(async () => {
    setLocationPermissionRequestLoading(true);

    try {
      await requestCurrentPosition();
      setLocationPermissionState("granted");
      setLocationPermissionError("");
      await refreshLocationPermissionState();
    } catch (error) {
      const nextState = resolveLocationPermissionStateFromError(error);
      setLocationPermissionState(nextState);
      setLocationPermissionError(getLocationPermissionErrorMessage(error));
    } finally {
      setLocationPermissionRequestLoading(false);
    }
  }, [refreshLocationPermissionState]);

  const handleRecheckLocationPermission = useCallback(async () => {
    setLocationPermissionRequestLoading(true);

    try {
      await refreshLocationPermissionState({ probeCurrentPosition: true });
    } finally {
      setLocationPermissionRequestLoading(false);
    }
  }, [refreshLocationPermissionState]);

  useDriverLiveActivity({
    pharmacyId,
    route: activityRoute || null,
    enabled: Boolean(
      pharmacyId
      && driverID
      && isOtpVerified
      && !isOtpVerificationScreen
      && (!shouldEnforceLocationPermission || isLocationAccessGranted)
      && !routeInterruption
    ),
  });

  // Keep dark mode off: remove 'dark' class so Flowbite/Tailwind dark: styles never apply
  useEffect(() => {
    document.documentElement.classList.remove("dark");
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === "production" || typeof window === "undefined") {
      return undefined;
    }

    const handleUnhandledRejection = (event) => {
      if (!isIgnorableAblyConnectionCloseError(event?.reason)) {
        return;
      }

      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      (window.location?.search || "").includes("flutter_app=1")
    ) {
      try {
        sessionStorage.setItem("flutter_app", "1");
      } catch (_) {}
    }
  }, []);

  useEffect(() => {
    if (!shouldEnforceLocationPermission) {
      setLocationPermissionState("granted");
      setLocationPermissionError("");
      return undefined;
    }

    void refreshLocationPermissionState({
      probeCurrentPosition: shouldUseDirectLocationProbe || !navigator?.permissions?.query,
    });

    const handleAppWake = () => {
      void refreshLocationPermissionState({
        probeCurrentPosition: showLocationPermissionLock,
      });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        handleAppWake();
      }
    };

    window.addEventListener("focus", handleAppWake);
    window.addEventListener("pageshow", handleAppWake);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("resume", handleAppWake);

    return () => {
      window.removeEventListener("focus", handleAppWake);
      window.removeEventListener("pageshow", handleAppWake);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("resume", handleAppWake);

      if (locationPermissionStatusRef.current) {
        locationPermissionStatusRef.current.onchange = null;
      }
    };
  }, [
    refreshLocationPermissionState,
    shouldEnforceLocationPermission,
    shouldUseDirectLocationProbe,
    showLocationPermissionLock,
  ]);

  // useEffect(() => {
  //   if (!showLocationPermissionLock) {
  //     return undefined;
  //   }

  //   const previousBodyOverflow = document.body.style.overflow;
  //   const previousBodyOverscroll = document.body.style.overscrollBehavior;
  //   const previousHtmlOverflow = document.documentElement.style.overflow;

  //   document.body.style.overflow = "hidden";
  //   document.body.style.overscrollBehavior = "none";
  //   document.documentElement.style.overflow = "hidden";

  //   return () => {
  //     document.body.style.overflow = previousBodyOverflow;
  //     document.body.style.overscrollBehavior = previousBodyOverscroll;
  //     document.documentElement.style.overflow = previousHtmlOverflow;
  //   };
  // }, [showLocationPermissionLock]);

  const clearBrokenDriverSession = useCallback(() => {
    try {
      sessionStorage.setItem(DRIVER_SESSION_EXPIRED_MESSAGE_KEY, DRIVER_SESSION_EXPIRED_MESSAGE);
    } catch (error) {
      void error;
      // Session storage can be unavailable in strict browser modes; the redirect still repairs auth.
    }

    Cookies.remove("authToken");
    Cookies.remove("verified");
    clearStoredRouteState();
    persistor.purge();
    navigate(routeNames.HOME_ROUTE);
  }, [navigate]);

  useEffect(() => {
    const token = authToken();
    if (!token || authLoginResponse?.loading) {
      return;
    }

    const persistRootExists =
      typeof localStorage === "undefined" || Boolean(localStorage.getItem("persist:root"));
    const authData = authLoginResponse?.data?.data;
    const hasDriverIdentity = Boolean(
      authData?.driver_profile?.id ||
      authData?.driver_pharmacist_user?.id ||
      authData?.id
    );

    if (!persistRootExists || !hasDriverIdentity) {
      clearBrokenDriverSession();
    }
  }, [
    authLoginResponse?.data?.data,
    authLoginResponse?.data?.data?.driver_pharmacist_user?.id,
    authLoginResponse?.data?.data?.driver_profile?.id,
    authLoginResponse?.data?.data?.id,
    authLoginResponse?.loading,
    clearBrokenDriverSession,
  ]);

  useEffect(() => {
    routeInterruptionActiveRef.current = Boolean(routeInterruption?.id);
  }, [routeInterruption?.id]);

  const redirectToRoutePicker = useCallback(() => {
    clearStoredRouteState();
    setRouteInterruption(null);

    const targetPath = `${routeNames.DASHBOARD_PHARMACY}?openRoutePicker=1`;
    if (`${location.pathname}${location.search}` !== targetPath) {
      navigate(targetPath);
    }
  }, [location.pathname, location.search, navigate]);

  const openRouteControlForLogoutBlocked = useCallback(() => {
    dispatch(clearLogoutBlockedMessage());

    const targetPath = `${routeNames.DASHBOARD_PHARMACY}?openRouteControl=1`;
    if (`${location.pathname}${location.search}` !== targetPath) {
      navigate(targetPath);
    }
  }, [dispatch, location.pathname, location.search, navigate]);

  const syncRouteAccessAfterRelease = useCallback(async ({ forceDashboardPicker = false } = {}) => {
    if (!pharmacyId || !driverID) {
      return;
    }

    if (routeInterruptionActiveRef.current) {
      return;
    }

    try {
      const activeRoutes = await fetchDriverActiveRoutes(pharmacyId);
      const activeRouteSet = new Set(
        (Array.isArray(activeRoutes) ? activeRoutes : [])
          .map((route) => `${route?.route ?? ""}`.trim())
          .filter(Boolean)
      );
      const currentRoute = `${new URLSearchParams(location.search).get("route") ?? ""}`.trim();
      const onRoutePage = location.pathname === routeNames.DRIVER_ROUTE_TODAY
        || location.pathname === routeNames.DRIVER_ROUTE_DELIVERED;

      if (onRoutePage && currentRoute && !activeRouteSet.has(currentRoute)) {
        redirectToRoutePicker();
        return;
      }

      if (
        forceDashboardPicker
        && location.pathname === routeNames.DASHBOARD_PHARMACY
        && activeRouteSet.size === 0
      ) {
        redirectToRoutePicker();
      }
    } catch (error) {
      void error;
    }
  }, [driverID, location.pathname, location.search, pharmacyId, redirectToRoutePicker]);

  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [fcmToken, setFcmToken] = useState(() => getStoredToken() || "");
  const [fcmCopyFeedback, setFcmCopyFeedback] = useState(false);
  const currentVideoCallUserId =
    authLoginResponse?.data?.data?.driver_profile?.id ??
    authLoginResponse?.data?.data?.driver_pharmacist_user?.id ??
    authLoginResponse?.data?.data?.id;

  // Setup push notifications; show enable-notification sheet only when needed, after app has loaded (smooth)
  useEffect(() => {
    setupPushNotifications().catch(() => {});

    if (!("Notification" in window) || Notification.permission === "granted")
      return;

    const dismissedKey = "notification-banner-dismissed-session";
    if (sessionStorage.getItem(dismissedKey)) return;

    const isIOS = isIOSSafari();
    const showBanner = () => {
      if (Notification.permission === "granted") return;
      setShowNotificationBanner(true);
    };

    if (Notification.permission === "denied") {
      setTimeout(showBanner, 1500);
      return;
    }
    if (isIOS) {
      setTimeout(showBanner, 2000);
    } else {
      setTimeout(showBanner, 4000);
    }
  }, []);

  const handleRequestNotificationPermission = async () => {
    const result = await requestNotificationPermission();
    if (result) {
      setShowNotificationBanner(false);
      const inFlutterApp =
        typeof window !== "undefined" &&
        (window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
          (window.location?.search || "").includes("flutter_app=1") ||
          sessionStorage?.getItem("flutter_app") === "1");
      if (inFlutterApp) return;
      // Generate FCM token when user enables notifications; save to API only if different from saved
      try {
        const token = await requestForToken();
        if (token) {
          setFcmToken(token);
          const apiToken = authLoginResponse?.data?.data?.fcm_token ?? null;
          const same =
            apiToken && String(token).trim() === String(apiToken).trim();
          if (!same) await saveFcmTokenToBackend(token);
        }
      } catch (err) {
        void err;
      }
    }
  };

  const handleDismissNotificationBanner = () => {
    sessionStorage.setItem("notification-banner-dismissed-session", "1");
    setShowNotificationBanner(false);
  };

  const [isOnline, setIsOnline] = useState(window.navigator.onLine);

  // When pharmacist is in an active call, subscribe so we close when driver ends the call
  useEffect(() => {
    const isOpen = pharmacistVideoCall.isOpen;
    const callId = pharmacistVideoCall.callData?.callId;
    if (!isOpen || !callId) {
      safeCloseAblyRealtime(pharmacistSessionAblyRef.current);
      pharmacistSessionAblyRef.current = null;
      return;
    }
    const ably = subscribeToVideoCallSession(
      callId,
      () => {
        setPharmacistVideoCall({ isOpen: false, callData: null });
      },
      { pharmacyId },
    );
    pharmacistSessionAblyRef.current = ably;
    return () => {
      safeCloseAblyRealtime(ably);
      pharmacistSessionAblyRef.current = null;
    };
  }, [pharmacistVideoCall.isOpen, pharmacistVideoCall.callData?.callId, pharmacyId]);

  useEffect(() => {
    const handleOnlineStatusChange = () => {
      setIsOnline(window.navigator.onLine);
    };

    window.addEventListener("online", handleOnlineStatusChange);
    window.addEventListener("offline", handleOnlineStatusChange);

    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
    };
  }, []);

  // useEffect(() => {

  // 	const ably = new Ably.Realtime({ key: EndPoints.apiKey });
  // 	const channel = ably.channels.get('oat-pharmacy-inspection-mode');

  // 	channel.subscribe(`inspection-${pharmacyId}`, (message) => {
  // 		if (message.name === `inspection-${pharmacyId}` && pharmacyId && token) {
  // 			dispatch(fetchPharmacyDetails(pharmacyId))
  // 		}
  // 	})

  // 	return () => {
  // 		if (ably.connection.state === 'connected') {
  // 			ably.close();
  // 		}
  // 	}
  // }, []);

  useEffect(() => {
    window.addEventListener(
      "scroll",
      () => {
        const metaTag = document.querySelector('meta[name="theme-color"]');
        if (window.scrollY / window.innerHeight > 0.8) {
          metaTag.setAttribute("content", "#FDF8F7");
        } else {
          metaTag.setAttribute("content", "#FDF8F7");
        }
      },
      false,
    );
  }, [pharmacyId]);

  useEffect(() => {
    if (!EndPoints.apiKey || !pharmacyId) {
      return;
    }

    const ably = getSharedDriverAppRealtime();
    if (!ably) {
      return undefined;
    }
    const channel = ably.channels.get("flexy-delivery");
    const eventName = `flexy-delivery-${pharmacyId}`;
    const handler = (message) => {
      dispatch(storeFlexiMarkedNotification(message.data));
    };

    channel.subscribe(eventName, handler);

    return () => {
      channel.unsubscribe(eventName, handler);
    };
  }, [dispatch, pharmacyId]);

  useEffect(() => {
    if (!EndPoints.apiKey || !currentVideoCallUserId) {
      return;
    }

    primeVideoCallRealtimeConnection().catch(() => {});
  }, [currentVideoCallUserId, pharmacyId]);

  useEffect(() => {
    if (!pharmacyId || !driverID || !isOtpVerified || isOtpVerificationScreen) {
      return undefined;
    }

    const formatPatients = (patients = []) =>
      patients
        .map((patient) => {
          const rxNumbers = Array.isArray(patient?.rxnumbers)
            ? patient.rxnumbers.filter(Boolean).join(", ")
            : "";
          return rxNumbers
            ? `${patient?.patient_name || "Patient"}: ${rxNumbers}`
            : patient?.patient_name || "Patient";
        })
        .join("\n");

    const getHandoverScopeLabel = (payload = {}) => {
      if (payload?.handover_type === "red_bag") {
        return "Returned stock handover";
      }

      if (payload?.handover_type === "route") {
        return Array.isArray(payload.routes) && payload.routes.length
          ? payload.routes.join(", ")
          : "Route handover";
      }

      if (payload?.handover_type === "patient") {
        return "Patient handover";
      }

      return "Medication handover";
    };

    const handleDeliveryRealtimeEvent = async (payload) => {
        if (!payload?.type) {
          return;
        }

        if (isOwnDriverHandoverRequest(payload, driverID)) {
          return;
        }

        const matchesHandoverRecipient = handoverRecipientTargets.some(
          (target) =>
            payload.type === "delivery_handover_request" &&
            payload.target_type === target.type &&
            Number(payload.target_id) === Number(target.id)
        );

        if (matchesHandoverRecipient) {
          if (handledDriverHandoverRequestsRef.current.has(payload.request_id)) {
            return;
          }

          handledDriverHandoverRequestsRef.current.add(payload.request_id);
          const routes = getHandoverScopeLabel(payload);
          const patients = formatPatients(payload.patients || []);
          setHandoverRequestError(null);
          setHandoverRequestSuccess(null);
          setReceiverSignature("");
          setIncomingHandoverRequest({
            requestId: payload.request_id,
            requestedByName: payload.requested_by?.name || "Another user",
            routes,
            patients,
            senderSignature: payload.sender_signature || null,
          });
          return;
        }

        if (
          payload.type === "patient_spotted_notification" &&
          Number(payload.target_driver_id) === Number(driverID)
        ) {
          const dedupeKey = `${payload.patient?.id || "patient"}-${payload.timestamp || ""}`;
          if (handledPatientSpottedEventsRef.current.has(dedupeKey)) {
            return;
          }

          handledPatientSpottedEventsRef.current.add(dedupeKey);
          const patientName = [payload.patient?.last_name, payload.patient?.first_name]
            .filter(Boolean)
            .join(", ")
            .trim();
          setPatientSpottedNotification({
            patientName: patientName || "Unknown patient",
            route: payload.patient?.route || "",
            seenBy: payload.seen_by?.name || "Another delivery user",
            latitude: payload.location?.latitude,
            longitude: payload.location?.longitude,
            locationAddress: null,
          });
          return;
        }

        if (
          payload.type === "delivery_live_location_request" &&
          Number(payload.target_driver_id) === Number(driverID)
        ) {
          try {
            const position = await requestCurrentPosition();
            const latitude = position?.coords?.latitude;
            const longitude = position?.coords?.longitude;
            const resolvedAddress = await reverseGeocode(latitude, longitude);

            await updateDriverLiveLocation({
              pharmacyId,
              route: Array.isArray(payload?.routes) && payload.routes.length === 1
                ? payload.routes[0]
                : "",
              latitude,
              longitude,
              accuracyMeters: position?.coords?.accuracy,
              speedMps: position?.coords?.speed,
              heading: position?.coords?.heading,
              requestContext: "live_location_request",
              requestedByPharmacyUserId: payload?.requested_by?.id,
              requestedByName: payload?.requested_by?.name,
              resolvedAddress,
            });
          } catch (error) {
            try {
              await denyDriverLiveLocationRequest({
                pharmacyId,
                route: Array.isArray(payload?.routes) && payload.routes.length === 1
                  ? payload.routes[0]
                  : "",
                requestedByPharmacyUserId: payload?.requested_by?.id,
                requestedByName: payload?.requested_by?.name,
              });
            } catch (_) {}
          }
          return;
        }
        };

    // Ably (existing path) + Socket.IO (parallel, more reliable on Android). Both feed
    // the SAME handler; handover request pop-ups de-dupe by request_id above, so a
    // handover delivered on both transports only shows once. The Ably path is unchanged.
    const subscription = subscribeToDeliveryVideoCallNotifications({
      pharmacyId,
      onEvent: handleDeliveryRealtimeEvent,
    });
    const socketSubscription = subscribeToDriverHandoverSocketUpdates({
      pharmacyId,
      onEvent: handleDeliveryRealtimeEvent,
    });

    return () => {
      subscription?.close();
      socketSubscription?.close();
    };
  }, [driverID, handoverRecipientTargets, isOtpVerified, isOtpVerificationScreen, pharmacyId]);

  // Global handover response (accepted/rejected) — show notification on any page
  useEffect(() => {
    if (
      !pharmacyId ||
      !isOtpVerified ||
      isOtpVerificationScreen ||
      handoverRecipientTargets.length === 0 ||
      !EndPoints.apiKey
    ) {
      return undefined;
    }
    const ably = getSharedDriverAppRealtime();
    if (!ably) {
      return undefined;
    }
    const channel = ably.channels.get("pharmacy-notifications");
    const eventName = `pharmacy-${pharmacyId}`;
    const handler = (messageEvent) => {
      const eventData = messageEvent?.data;
      if (eventData?.type !== "delivery_handover_response") return;

      const matchesCancelledRecipient = handoverRecipientTargets.some(
        (target) =>
          eventData?.status === "cancelled" &&
          eventData?.target_type === target.type &&
          Number(eventData?.target_id) === Number(target.id) &&
          Number(eventData?.request_id) === Number(incomingHandoverRequest?.requestId)
      );

      if (matchesCancelledRecipient) {
        setIncomingHandoverRequest(null);
        setHandoverRequestError(null);
        setHandoverRequestSuccess(null);
      }

      const requestedById = eventData?.requested_by_driver_id;
      if (!requestedById || Number(requestedById) !== Number(driverID)) return;
      const status = eventData?.status;
      const responderName = eventData?.responded_by?.name || "Pharmacist";
      if (status === "accepted") {
        setHandoverNotification({ type: "accepted", responderName });
      } else if (status === "rejected") {
        setHandoverNotification({ type: "rejected", responderName });
      }
    };
    channel.subscribe(eventName, handler);
    return () => {
      channel.unsubscribe(eventName, handler);
      if (handoverNotificationTimerRef.current) clearTimeout(handoverNotificationTimerRef.current);
    };
  }, [
    driverID,
    handoverRecipientTargets,
    incomingHandoverRequest?.requestId,
    isOtpVerified,
    isOtpVerificationScreen,
    pharmacyId,
  ]);

  const loadRouteInterruption = useCallback(async () => {
    if (!pharmacyId || !driverID || !isOtpVerified || isOtpVerificationScreen) {
      setRouteInterruption(null);
      return null;
    }

    try {
      const interruption = await fetchDriverRouteInterruptionStatus(pharmacyId);
      const nextInterruption = interruption || null;

      if (
        nextInterruption &&
        dismissedRouteInterruptionIdRef.current &&
        Number(nextInterruption.id) === Number(dismissedRouteInterruptionIdRef.current)
      ) {
        setRouteInterruption(null);
        return null;
      }

      if (
        !nextInterruption ||
        !dismissedRouteInterruptionIdRef.current ||
        Number(nextInterruption.id) !== Number(dismissedRouteInterruptionIdRef.current)
      ) {
        dismissedRouteInterruptionIdRef.current = null;
      }

      setRouteInterruption(nextInterruption);
      return nextInterruption;
    } catch (error) {
      void error;
      return null;
    }
  }, [driverID, pharmacyId, isOtpVerificationScreen, isOtpVerified]);


  useEffect(() => {
    if (!pharmacyId || !driverID || !EndPoints.apiKey) {
      return undefined;
    }

    const ably = getSharedDriverAppRealtime();
    if (!ably) {
      return undefined;
    }
    const channel = ably.channels.get("pharmacy-notifications");
    const eventName = `pharmacy-${pharmacyId}`;
    const handler = (messageEvent) => {
      const eventData = messageEvent?.data;

      // The pharmacy took a route back. Say so, because otherwise the route
      // just disappears from under the driver with no explanation.
      if (eventData?.type === "delivery_route_released") {
        if (Number(eventData?.target_driver_id) !== Number(driverID)) {
          return;
        }

        setRouteReleasedNotice({
          route: `${eventData?.route ?? ""}`.trim(),
          releasedByName: eventData?.released_by?.name || "The pharmacy",
        });
        loadRouteInterruption();
        syncRouteAccessAfterRelease({ forceDashboardPicker: true });
        return;
      }

      if (eventData?.type !== "audit_trail_updated") {
        return;
      }

      if (!routeReleaseEventTypes.has(eventData?.event_type)) {
        return;
      }

      const actorName = `${eventData?.actor?.name ?? ""}`.trim().toLowerCase();
      const isCurrentDriverRelease = Boolean(actorName && driverName && actorName === driverName);

      loadRouteInterruption();
      syncRouteAccessAfterRelease({ forceDashboardPicker: isCurrentDriverRelease });
    };

    channel.subscribe(eventName, handler);
    return () => {
      channel.unsubscribe(eventName, handler);
    };
  }, [
    driverID,
    driverName,
    loadRouteInterruption,
    pharmacyId,
    syncRouteAccessAfterRelease,
  ]);

  useEffect(() => {
    if (!pharmacyId || !driverID || !EndPoints.apiKey) {
      return undefined;
    }

    const ably = getSharedDriverAppRealtime();
    if (!ably) {
      return undefined;
    }

    const channel = ably.channels.get("pharmacy-notifications");
    const eventName = `pharmacy-${pharmacyId}`;
    const handler = (messageEvent) => {
      const eventData = messageEvent?.data;
      if (eventData?.type !== "delivery_prescriptions_pharmacist_signed") {
        return;
      }

      if (Number(eventData?.pharmacy_id) !== Number(pharmacyId)) {
        return;
      }

      dispatch(markPatientPrescriptionsPharmacistSigned(eventData));
    };

    channel.subscribe(eventName, handler);
    return () => {
      channel.unsubscribe(eventName, handler);
    };
  }, [dispatch, driverID, pharmacyId]);

  const dismissHandoverNotification = useCallback(() => {
    if (handoverNotificationTimerRef.current) clearTimeout(handoverNotificationTimerRef.current);
    handoverNotificationTimerRef.current = null;
    setHandoverNotification(null);
  }, []);

  const dismissIncomingHandoverRequest = useCallback(() => {
    setIncomingHandoverRequest(null);
    setReceiverSignature("");
    setHandoverRequestError(null);
    setHandoverRequestSuccess(null);
  }, []);

  const dismissPatientSpottedNotification = useCallback(() => {
    setPatientSpottedNotification(null);
  }, []);

  // Resolve lat/long to address when patient spotted notification is shown
  useEffect(() => {
    const notification = patientSpottedNotification;
    if (!notification || notification.locationAddress !== null) return;
    const lat = notification.latitude;
    const lon = notification.longitude;
    if (lat == null || lon == null) return;
    let cancelled = false;
    reverseGeocode(lat, lon).then((address) => {
      if (cancelled) return;
      setPatientSpottedNotification((prev) =>
        prev && prev.latitude === lat && prev.longitude === lon
          ? { ...prev, locationAddress: address }
          : prev
      );
    });
    return () => { cancelled = true; };
  }, [patientSpottedNotification?.latitude, patientSpottedNotification?.longitude, patientSpottedNotification?.locationAddress]);


  useEffect(() => {
    loadRouteInterruption();
  }, [loadRouteInterruption]);

  useEffect(() => {
    if (!pharmacyId || !driverID || !isOtpVerified || isOtpVerificationScreen) {
      return;
    }

    if (
      location.pathname === routeNames.DRIVER_ROUTE_TODAY
      || location.pathname === routeNames.DRIVER_ROUTE_DELIVERED
    ) {
      syncRouteAccessAfterRelease();
    }
  }, [
    driverID,
    isOtpVerificationScreen,
    isOtpVerified,
    location.pathname,
    location.search,
    pharmacyId,
    syncRouteAccessAfterRelease,
  ]);

  useEffect(() => {
    setRouteResumeError(null);
    setRouteResumeExplanation("");
  }, [routeInterruption?.id]);

  const handleResumeInterruptedRoutes = useCallback(async () => {
    const interruptionId = routeInterruption?.id;
    const explanation = routeResumeExplanation.trim();

    if (!interruptionId) return;

    if (!explanation) {
      setRouteResumeError("An explanation is required before resuming work.");
      return;
    }

    setRouteResumeLoading(true);
    setRouteResumeError(null);

    try {
      await resumeDriverRouteInterruption(interruptionId, explanation);
      recordDriverActivitySignal({
        pharmacyId,
        route: activityRoute || routeInterruption?.routes?.[0] || undefined,
      });
      dismissedRouteInterruptionIdRef.current = interruptionId;
      setRouteInterruption(null);
      setRouteResumeExplanation("");
    } catch (error) {
      setRouteResumeError(
        error?.response?.data?.message ||
        error?.response?.data?.error?.resume_explanation?.[0] ||
        error?.response?.data?.error?.resume?.[0] ||
        "Unable to resume this route interruption right now."
      );
      await loadRouteInterruption();
    } finally {
      setRouteResumeLoading(false);
    }
  }, [activityRoute, loadRouteInterruption, pharmacyId, routeInterruption?.id, routeInterruption?.routes, routeResumeExplanation]);

  useEffect(() => {
    if (!driverID || !pharmacyId || !apiBaseUrl || !isOtpVerified || isOtpVerificationScreen) {
      return undefined;
    }

    const authToken = Cookies.get("authToken");
    if (!authToken) {
      return undefined;
    }

    const endpoint = `${apiBaseUrl}/${EndPoints.driver_route_interruptions}/report`;
    const handlePageHide = () => {
      fetch(endpoint, {
        method: "POST",
        keepalive: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ pharmacy_id: pharmacyId }),
      }).catch(() => {});
    };

    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [driverID, pharmacyId, isOtpVerificationScreen, isOtpVerified]);

  const handleAcceptHandoverRequest = useCallback(async (signatureBase64) => {
    if (!incomingHandoverRequest?.requestId) return;
    if (!signatureBase64) {
      setHandoverRequestError("Signature is required.");
      return false;
    }
    setHandoverRequestActionLoading(true);
    setHandoverRequestError(null);
    try {
      await acceptDriverHandoverRequest(incomingHandoverRequest.requestId, signatureBase64);
      recordDriverActivitySignal({ pharmacyId });
      setReceiverSignature("");
      setHandoverRequestSuccess(incomingHandoverRequest.requestedByName);
      return true;
    } catch (error) {
      setHandoverRequestError(
        error?.response?.data?.message || "Unable to accept the handover request right now."
      );
      return false;
    } finally {
      setHandoverRequestActionLoading(false);
    }
  }, [incomingHandoverRequest, pharmacyId]);

  const handleRejectHandoverRequest = useCallback(async () => {
    if (!incomingHandoverRequest?.requestId) return;
    setHandoverRequestActionLoading(true);
    setHandoverRequestError(null);
    try {
      await rejectDriverHandoverRequest(incomingHandoverRequest.requestId);
      recordDriverActivitySignal({ pharmacyId });
      dismissIncomingHandoverRequest();
    } catch (error) {
      setHandoverRequestError(
        error?.response?.data?.message || "Unable to reject the handover request."
      );
    } finally {
      setHandoverRequestActionLoading(false);
    }
  }, [incomingHandoverRequest, dismissIncomingHandoverRequest, pharmacyId]);

  // Refs to avoid duplicate save and to allow send again when a different user logs in
  const lastFcmTokenSentRef = useRef(null);
  const fcmEffectUserIdRef = useRef(null);

  // When user is logged in: get FCM token and save to API whenever token is obtained or updated.
  // Runs on login, on page refresh, and when token changes. Keeps API in sync with current device token.
  useEffect(() => {
    const authToken = Cookies.get("authToken");
    if (!authToken) return;
    if (!authLoginResponse?.data?.data) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    const inFlutterApp =
      typeof window !== "undefined" &&
      (window.__FLUTTER_NATIVE_VIDEO_CALLS__ ||
        (window.location?.search || "").includes("flutter_app=1") ||
        sessionStorage?.getItem("flutter_app") === "1");
    if (inFlutterApp) return;

    const userId = authLoginResponse?.data?.data?.driver_profile?.id;
    if (userId != null && fcmEffectUserIdRef.current !== userId) {
      fcmEffectUserIdRef.current = userId;
      lastFcmTokenSentRef.current = null; // New user: always send token
    }

    let cancelled = false;
    isFirebaseMessagingAvailable()
      .then((supported) => {
        if (!supported || cancelled) return null;
        return requestForToken();
      })
      .then((currentToken) => {
        if (cancelled || !currentToken) return;
        setFcmToken(currentToken);
        // Save to API whenever we have a token: on refresh, on login, or when token updates
        if (lastFcmTokenSentRef.current === currentToken) return; // Avoid duplicate POST in same session
        lastFcmTokenSentRef.current = currentToken;
        saveFcmTokenToBackend(currentToken).catch(() => {});
      })
      .catch((err) => {
        void err;
      });
    return () => {
      cancelled = true;
    };
  }, [authLoginResponse?.data?.data?.driver_profile?.id]);

  // Sync FCM token from storage to state for UI (e.g. after refresh before effect runs)
  useEffect(() => {
    const stored = getStoredToken();
    if (stored) setFcmToken(stored);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.DriverApp) return;
    const authToken = Cookies.get("authToken") ?? null;
    const data = authLoginResponse?.data?.data;
    const pharmacistName =
      pharmacistNameForSelectedPharmacy ??
      data?.driver_pharmacist_user?.name ??
      data?.name ??
      null;
    const driverName =
      data?.driver_profile?.name ?? data?.name ?? pharmacistName ?? null;
    const videoCallContext =
      pharmacyId &&
      pharmacistIdForSelectedPharmacy != null &&
      driverID &&
      EndPoints.apiKey
        ? {
            pharmacyId: String(pharmacyId),
            pharmacistId: String(pharmacistIdForSelectedPharmacy),
            driverID: String(driverID),
            isPharmacist: !!isPharmacistForSelectedPharmacy,
            driverName: driverName || "",
            pharmacistName: pharmacistName || "",
            ablyApiKey: EndPoints.apiKey,
          }
        : null;
    try {
      window.DriverApp.postMessage(
        JSON.stringify({
          type: "auth",
          authToken: authToken || null,
          // Tell native which backend we use so it also registers the FCM token
          // there (handover push is sent from this backend).
          apiBaseUrl: process.env.REACT_APP_BASE_URL || null,
          videoCallContext: videoCallContext ?? null,
        }),
      );
    } catch (_) {}
  }, [
    authLoginResponse?.data?.data,
    driverID,
    isPharmacistForSelectedPharmacy,
    pharmacistIdForSelectedPharmacy,
    pharmacistNameForSelectedPharmacy,
    pharmacyId,
  ]);

  const handleCopyFcmToken = () => {
    if (!fcmToken) return;
    navigator.clipboard.writeText(fcmToken).then(() => {
      setFcmCopyFeedback(true);
      setTimeout(() => setFcmCopyFeedback(false), 2000);
    });
  };

  // Let the notification worker ignore a call started by this signed-in driver.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return undefined;
    }

    const message = {
      type: "SET_VIDEO_CALL_NOTIFICATION_IDENTITY",
      driverId: driverID != null ? String(driverID) : null,
    };

    navigator.serviceWorker.controller?.postMessage(message);
    navigator.serviceWorker.ready
      .then((registration) => registration.active?.postMessage(message))
      .catch(() => {});

    return undefined;
  }, [driverID]);

  // Foreground FCM: show video call UI when payload is a video call; otherwise skip or optional toast
  useEffect(() => {
    let cancelled = false;
    const listen = async () => {
      if (cancelled) return;

      const supported = await isFirebaseMessagingAvailable();
      if (!supported || cancelled) return;

      onMessageListener()
        .then((payload) => {
          if (cancelled) return;
          const data = payload?.data;
          if (data?.callId) {
            const normalized = {
              ...data,
              pharmacistId:
                data.pharmacistId != null
                  ? Number(data.pharmacistId)
                  : data.pharmacistId,
              patientId:
                data.patientId != null
                  ? Number(data.patientId)
                  : data.patientId,
              driverId:
                data.driverId != null ? String(data.driverId) : data.driverId,
              status: data.status || "requesting",
            };
            window.dispatchEvent(
              new CustomEvent("videoCallRequestFromFCM", {
                detail: { data: normalized },
              }),
            );
          }
          listen();
        })
        .catch((err) => {
          void err;
          setTimeout(listen, 500);
        });
    };
    listen();
    return () => {
      cancelled = true;
    };
  }, []);

  // Store pending FCM notification click (app opened from notification when not in recent)
  // So VideoCallRequestHandler can show incoming-call modal once auth is ready
  const pendingFcmClickRef = useRef(null);
  const appliedPendingCallIdRef = useRef(null);
  const fcmClickFromUrlRef = useRef(null);
  const FCM_PENDING_STORAGE_KEY = "fcm_pending_click";
  if (typeof window !== "undefined") {
    const q = new URLSearchParams(window.location.search).get("fcm_click");
    if (q) fcmClickFromUrlRef.current = q;
  }
  const readPendingNotificationClick = useCallback(() => {
    const pending = pendingFcmClickRef.current;
    if (pending) return pending;
    try {
      const stored = sessionStorage.getItem(FCM_PENDING_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (_) {
      return null;
    }
  }, []);

  const clearPendingNotificationClick = useCallback(() => {
    pendingFcmClickRef.current = null;
    try {
      sessionStorage.removeItem(FCM_PENDING_STORAGE_KEY);
    } catch (_) {}
  }, []);

  const takePendingNotificationClickForDeliveryCall = useCallback(() => {
    const pending = readPendingNotificationClick();
    if (!pending) return null;
    if (pending?.data?.flow === "pharmacist_to_driver") return null;
    clearPendingNotificationClick();
    return pending;
  }, [clearPendingNotificationClick, readPendingNotificationClick]);

  const takePendingNotificationClickForPharmacistDriver = useCallback(() => {
    const pending = readPendingNotificationClick();
    if (pending?.data?.flow !== "pharmacist_to_driver") return null;
    clearPendingNotificationClick();
    return pending;
  }, [clearPendingNotificationClick, readPendingNotificationClick]);

  const handlePharmacistAccept = useCallback((callData) => {
    setPharmacistVideoCall({
      isOpen: true,
      callData,
    });
  }, []);

  const handlePharmacistDecline = useCallback(() => {}, []);

  // When app loads after user clicked FCM notification, ask service worker for pending click data.
  // Don't rely only on controller (it's null on cold start). Also message via getRegistrations().active.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const applyPending = (pending) => {
      if (!pending?.data?.callId) return;
      const callId = pending.data.callId;
      if (appliedPendingCallIdRef.current === callId) return;
      appliedPendingCallIdRef.current = callId;
      const data = {
        ...pending.data,
        pharmacistId:
          pending.data.pharmacistId != null
            ? Number(pending.data.pharmacistId)
            : pending.data.pharmacistId,
        patientId:
          pending.data.patientId != null
            ? Number(pending.data.patientId)
            : pending.data.patientId,
        driverId:
          pending.data.driverId != null
            ? String(pending.data.driverId)
            : pending.data.driverId,
      };
      const detail = { action: pending.action || "accept", data };
      pendingFcmClickRef.current = detail;
      try {
        sessionStorage.setItem(FCM_PENDING_STORAGE_KEY, JSON.stringify(detail));
      } catch (_) {}
      window.dispatchEvent(
        new CustomEvent("videoCallNotificationClick", { detail }),
      );
    };
    const onMessage = (event) => {
      if (event?.data?.type === "VIDEO_CALL_NOTIFICATION_CLICK") {
        applyPending({ action: event.data.action, data: event.data.data });
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    if (navigator.serviceWorker.startMessages)
      navigator.serviceWorker.startMessages();
    const sendGetPendingToWorker = (worker) => {
      if (!worker) return;
      const channel = new MessageChannel();
      channel.port1.onmessage = (e) => {
        const pending = e.data;
        if (pending?.data?.callId) applyPending(pending);
      };
      worker.postMessage({ type: "GET_PENDING_FCM_CLICK" }, [channel.port2]);
    };
    const tryGetPending = () => {
      // 1) If page is already controlled, use controller (fast path)
      if (navigator.serviceWorker.controller) {
        sendGetPendingToWorker(navigator.serviceWorker.controller);
        return;
      }
      // 2) Cold start: page not controlled yet. Message the SW via its registration (same SW that handled the click has an active worker).
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((reg) => {
          if (reg.active) sendGetPendingToWorker(reg.active);
        });
      });
    };
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("fcm_click") || fcmClickFromUrlRef.current;
    if (raw) {
      fcmClickFromUrlRef.current = null;
      try {
        let pending = null;
        try {
          pending = JSON.parse(decodeURIComponent(raw));
        } catch (_) {
          pending = JSON.parse(raw);
        }
        if (pending?.data?.callId) {
          applyPending(pending);
          params.delete("fcm_click");
          const clean =
            window.location.pathname +
            (params.toString() ? "?" + params.toString() : "") +
            window.location.hash;
          window.history.replaceState({}, "", clean);
        }
      } catch (_) {}
    }
    const delays = [0, 100, 300, 500, 1000, 2000, 3500];
    navigator.serviceWorker.ready.then(() => {
      delays.forEach((ms) => {
        if (ms === 0) tryGetPending();
        else setTimeout(tryGetPending, ms);
      });
    });
    return () =>
      navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  const locationPermissionMessage =
    locationPermissionState === "unsupported"
      ? "Location services are not supported on this device."
      : "Allow access when prompted. If no prompt appears, enable it from device or browser settings.";
  const locationPermissionButtonLabel = locationPermissionRequestLoading
    ? locationPermissionState === "prompt"
      ? "Opening..."
      : "Checking..."
    : "Continue";
  const handleLocationPermissionAction = locationPermissionState === "prompt"
    ? handleRequestLocationPermission
    : handleRecheckLocationPermission;
  const showLocationPermissionError =
    Boolean(locationPermissionError) &&
    locationPermissionState !== "denied" &&
    locationPermissionState !== "unsupported";

  return (
    <>
      {/* Mounted once: every delivery path raises the early-dose
          confirmation through the same prompt. */}
      <DoseGapOverrideModal />

      {/* {process.env.NODE_ENV === 'development' && process.env.REACT_SHOW_FIREBASE_TOKEN === 'true' && (
				<div className="p-3 bg-gray-50 border-b border-gray-200">
					<p className="text-sm font-medium text-gray-700 mb-1">FCM Token (this device)</p>
					<div className="flex gap-2 items-start">
						<pre className="flex-1 min-w-0 text-xs text-gray-600 bg-white border border-gray-200 rounded p-2 overflow-x-auto break-all">
							{fcmToken || 'No token yet. Allow notifications to generate.'}
						</pre>
						<button
							type="button"
							onClick={handleCopyFcmToken}
							disabled={!fcmToken}
							className="shrink-0 px-3 py-2 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{fcmCopyFeedback ? 'Copied!' : 'Copy'}
						</button>
					</div>
				</div>
			)} */}
      {updateAvailable && (
        // Blocking on purpose. The old bottom bar was dismissible, so drivers kept
        // working on a stale build and reported UI bugs that were already fixed.
        // z-index sits above every other overlay (offline modal is z-50).
        <div
          className="fixed inset-0 flex items-center justify-center z-[300] bg-black/40 backdrop-filter backdrop-blur-sm"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="update-required-title"
        >
          <div className="bg-white p-6 rounded-lg shadow-lg mx-6 max-w-sm w-full text-center">
            <div className="flex justify-center mb-4">
              <svg
                className="w-10 h-10 text-[#288f6c]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
            <p id="update-required-title" className="font-semibold text-lg mb-2">
              New version available
            </p>
            <p className="text-gray-600 mb-6">
              Please reload to get the latest version of the app.
            </p>
            <button
              type="button"
              className="w-full bg-[#288f6c] text-white font-semibold py-3 rounded-lg active:opacity-80 disabled:opacity-60"
              onClick={handleUpdateReload}
              disabled={isReloading}
            >
              {isReloading ? "Reloading…" : "Reload"}
            </button>
          </div>
        </div>
      )}

      {!isOnline && (
        <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-filter backdrop-blur-sm">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex items-center mb-4">
              <svg
                className="w-6 h-6 mr-3 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <p className="font-semibold text-lg">Connection Lost</p>
            </div>
            <p>Please check your internet connection.</p>
          </div>
        </div>
      )}

      {/* Enable Notifications sheet – shown after a short delay so app loads smoothly */}
      {!showLocationPermissionLock &&
        showNotificationBanner &&
        "Notification" in window &&
        Notification.permission !== "granted" && (
          <AnimatedBottomSheet
            isOpen
            onClose={handleDismissNotificationBanner}
            ariaLabel="Enable Notifications"
          >
                <div className="bg-white pb-8 pt-2 px-6 relative rounded-t-[28px] overflow-hidden">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-1 flex justify-center pt-1">
                      <div
                        className="w-12 h-1 rounded-full bg-gray-300"
                        aria-hidden
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleDismissNotificationBanner}
                      className="absolute right-6 top-3 p-1.5 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors -mt-0.5"
                      aria-label="Dismiss"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 mb-1.5 text-lg">
                        Enable Notifications
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {Notification.permission === "denied"
                          ? isIOSSafari()
                            ? "Notifications are blocked. Please enable them in Settings → Safari → Notifications to receive video call alerts."
                            : "Notifications are blocked. Please enable them in browser settings to receive video call alerts."
                          : isIOSSafari()
                            ? 'Tap "Enable Notifications" below to allow this site to send you notifications for video calls.'
                            : "Enable browser notifications to receive video call alerts even when the app is in the background."}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {Notification.permission === "default" && (
                      <button
                        type="button"
                        onClick={handleRequestNotificationPermission}
                        className="flex-1 min-w-0 inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs py-2 px-3 rounded-lg transition-colors"
                      >
                        Enable Notifications
                      </button>
                    )}
                    {Notification.permission === "denied" && (
                      <button
                        type="button"
                        onClick={() => {
                          if (isIOSSafari()) {
                            alert(
                              "To enable notifications on iOS Safari:\n\n1. Open Settings → Safari → Notifications\n2. Find this website and turn on Notifications\n3. Or: Settings → Safari → Website Settings → Notifications\n4. Refresh this page",
                            );
                          } else {
                            alert(
                              'To enable notifications:\n\n1. Click the lock or info icon in the address bar\n2. Find "Notifications" and set to Allow\n3. Refresh the page',
                            );
                          }
                        }}
                        className="flex-1 min-w-0 inline-flex items-center justify-center border-2 border-blue-600 text-blue-600 bg-white hover:bg-blue-50 font-semibold text-xs py-2 px-3 rounded-lg transition-colors"
                      >
                        Show Instructions
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleDismissNotificationBanner}
                      className="flex-1 min-w-0 inline-flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-xs py-2 px-3 rounded-lg transition-colors border border-gray-200"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
          </AnimatedBottomSheet>
        )}

      <AppRoutes />

      {showLocationPermissionLock && (
        <div
          className="fixed inset-0 z-[20000] flex items-end justify-center bg-slate-950/70 px-2.5 pt-4 sm:items-center sm:p-3 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="location-permission-lock-title"
        >
          <div className="w-full max-w-md overflow-hidden rounded-t-[24px] border border-white/80 bg-[#FDF8F7] shadow-[0_18px_56px_rgba(15,23,42,0.18)] sm:rounded-[24px]">
            <div className="relative overflow-hidden px-4 pb-3 pt-3.5 sm:px-5 sm:pb-4 sm:pt-4">
              <div className="absolute -right-14 -top-14 h-32 w-32 rounded-full bg-[#fdd3b7]/75 blur-3xl" />
              <div className="absolute -left-10 top-0 h-24 w-24 rounded-full bg-[#3a52bb]/15 blur-3xl" />
              <div className="relative">
                <div className="flex items-start gap-3">
                  <svg
                    className="mt-0.5 h-9 w-9 shrink-0 text-amber-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.75}
                    stroke="currentColor"
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                    />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <h2
                      id="location-permission-lock-title"
                      className="text-[30px] font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-[28px]"
                    >
                      ACCESS REQUIRED
                    </h2>
                    {locationPermissionState !== "unsupported" && (
                      <p className="mt-1 text-sm font-medium leading-5 text-slate-600">
                        Turn on location to continue.
                      </p>
                    )}
                    <p
                      className={`text-sm leading-5 text-slate-700 ${
                        locationPermissionState === "unsupported" ? "mt-1" : "mt-2"
                      }`}
                    >
                      {locationPermissionMessage}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 pb-3.5 sm:px-5 sm:pb-4">
              {showLocationPermissionError && (
                <p className="mt-2 text-sm leading-5 text-rose-600" aria-live="polite">
                  {locationPermissionError}
                </p>
              )}

              <button
                type="button"
                onClick={handleLocationPermissionAction}
                disabled={locationPermissionRequestLoading}
                className="mt-2.5 inline-flex w-full items-center justify-center rounded-[18px] bg-[#10b981] px-4 py-2.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-[#059669] disabled:cursor-not-allowed disabled:bg-[#6ee7b7]"
              >
                {locationPermissionButtonLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {routeInterruption && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="route-interruption-title"
        >
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">
              Route Interrupted
            </p>
            <h2 id="route-interruption-title" className="mt-2 text-xl font-semibold text-slate-900">
              Resume your reserved route before continuing
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Your previous route reservation was held for reconnect. Add an explanation before you resume work.
            </p>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <p>
                <span className="font-semibold text-slate-900">Routes:</span>{" "}
                {Array.isArray(routeInterruption.routes) && routeInterruption.routes.length > 0
                  ? routeInterruption.routes.join(", ")
                  : "-"}
              </p>
              <p className="mt-2">
                <span className="font-semibold text-slate-900">Interrupted:</span>{" "}
                {formatRouteInterruptionDateTime(
                  routeInterruption.interrupted_at,
                  routeInterruption.interrupted_at_display || "-"
                )}
              </p>
              <p className="mt-2">
                <span className="font-semibold text-slate-900">Resume by:</span>{" "}
                {formatRouteInterruptionDateTime(
                  routeInterruption.expires_at,
                  routeInterruption.expires_at_display || "-"
                )}
              </p>
            </div>
            <label className="mt-5 block text-sm font-medium text-slate-900" htmlFor="route-resume-explanation">
              Explanation
            </label>
            <textarea
              id="route-resume-explanation"
              value={routeResumeExplanation}
              onChange={(event) => setRouteResumeExplanation(event.target.value)}
              placeholder="Describe what interrupted the delivery work."
              className="mt-2 min-h-[132px] w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              disabled={routeResumeLoading}
            />
            {routeResumeError && (
              <p className="mt-3 text-sm text-red-600">{routeResumeError}</p>
            )}
            <button
              type="button"
              onClick={handleResumeInterruptedRoutes}
              disabled={routeResumeLoading}
              className="mt-5 w-full rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {routeResumeLoading ? "Resuming…" : "Resume Route Work"}
            </button>
          </div>
        </div>
      )}

      {/* Video Call Request Handler - for pharmacists to receive incoming calls */}
      {/* Component handles pharmacist detection internally */}
      <VideoCallRequestHandler
        getPendingNotificationClick={takePendingNotificationClickForDeliveryCall}
        isInActiveCall={
          pharmacistVideoCall.isOpen && !!pharmacistVideoCall.callData
        }
        onAccept={handlePharmacistAccept}
        onDecline={handlePharmacistDecline}
      />
      <PharmacistDriverIncomingCallHandler
        getPendingNotificationClick={takePendingNotificationClickForPharmacistDriver}
      />

      {/* Video Call Component for Pharmacist */}
      {pharmacistVideoCall.isOpen && pharmacistVideoCall.callData && (
        <VideoCallErrorBoundary
          onClose={() => {
            const callId = pharmacistVideoCall.callData?.callId;
            if (callId) {
              endDriverPharmacistDeliveryVideoCall(callId).catch(() => {});
              publishVideoCallEnded(callId, "pharmacist").catch(() => {});
            }
            if (
              pharmacistSessionAblyRef.current?.connection?.state ===
              "connected"
            ) {
              safeCloseAblyRealtime(pharmacistSessionAblyRef.current);
              pharmacistSessionAblyRef.current = null;
            }
            setPharmacistVideoCall({
              isOpen: false,
              callData: null,
            });
          }}
        >
          <VideoCall
            isOpen={pharmacistVideoCall.isOpen}
            onClose={() => {
            const callId = pharmacistVideoCall.callData?.callId;
            if (callId) {
                endDriverPharmacistDeliveryVideoCall(callId).catch(() => {});
                publishVideoCallEnded(callId, "pharmacist").catch(() => {});
              }
              if (
                pharmacistSessionAblyRef.current?.connection?.state ===
                "connected"
              ) {
                safeCloseAblyRealtime(pharmacistSessionAblyRef.current);
                pharmacistSessionAblyRef.current = null;
              }
              setPharmacistVideoCall({
                isOpen: false,
                callData: null,
              });
            }}
            patientData={{
              id: pharmacistVideoCall.callData.patientId,
              first_name:
                pharmacistVideoCall.callData.patientFirstName ??
                pharmacistVideoCall.callData.patientName
                  ?.split(" ")
                  .slice(1)
                  .join(" ") ??
                "",
              last_name:
                pharmacistVideoCall.callData.patientLastName ??
                pharmacistVideoCall.callData.patientName?.split(" ")[0] ??
                "",
              preferred_name:
                pharmacistVideoCall.callData.patientPreferredName ?? "",
            }}
            driverName={pharmacistVideoCall.callData.driverName}
            pharmacistName={
              authLoginResponse?.data?.data?.driver_pharmacist_user?.name ||
              authLoginResponse?.data?.data?.name ||
              "Pharmacist"
            }
            callState="accepted"
            isCaller={false}
            vonageSessionId={pharmacistVideoCall.callData.vonage_session_id}
            vonageToken={pharmacistVideoCall.callData.vonage_token}
            applicationId={pharmacistVideoCall.callData.application_id}
            videoProvider={pharmacistVideoCall.callData.video_provider}
            livekitUrl={pharmacistVideoCall.callData.livekit_url}
            autoScreenshot={true}
            onScreenshot={async (blob) => {
              // Upload screenshot to API as well (same as driver side)
              if (
                !blob ||
                !pharmacyId ||
                pharmacistVideoCall.callData?.patientId == null
              )
                return;
              try {
                await dispatch(
                  saveDeliveryCallImage({
                    pharmacy_id: pharmacyId,
                    patient_id: pharmacistVideoCall.callData.patientId,
                    imageBlob: blob,
                  }),
                ).unwrap();
              } catch (err) {}
            }}
          />
        </VideoCallErrorBoundary>
      )}

      {/* Global handover accepted/rejected notification — visible on any page */}
      {handoverNotification && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="handover-notification-title"
          onClick={dismissHandoverNotification}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-slate-800 px-6 py-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center">
              {handoverNotification.type === "accepted" ? (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-700">
                  <svg className="h-9 w-9 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-900/60">
                  <svg className="h-9 w-9 text-red-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              <p id="handover-notification-title" className="mt-5 text-base font-medium leading-snug text-slate-200">
                {handoverNotification.type === "accepted"
                  ? `Your handover request was accepted by ${handoverNotification.responderName}.`
                  : `Your handover request was rejected by ${handoverNotification.responderName}.`}
              </p>
              <button
                type="button"
                onClick={dismissHandoverNotification}
                className="mt-6 w-full rounded-xl bg-slate-600 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-500"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {routeReleasedNotice && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="route-released-notice-title"
          onClick={() => setRouteReleasedNotice(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-slate-800 px-6 py-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-900/60">
                <svg className="h-9 w-9 text-amber-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <p id="route-released-notice-title" className="mt-5 text-base font-medium leading-snug text-slate-200">
                {routeReleasedNotice.releasedByName} took route{" "}
                {routeReleasedNotice.route || "-"} off you. Please bring anything still in your bag
                back to the pharmacy.
              </p>
              <button
                type="button"
                onClick={() => setRouteReleasedNotice(null)}
                className="mt-6 w-full rounded-xl bg-slate-600 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-500"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Incoming handover request — in-app modal (no browser alert) */}
      <AnimatePresence initial={false}>
        {incomingHandoverRequest ? (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="incoming-handover-title"
          onClick={() => !handoverRequestActionLoading && !handoverRequestSuccess && dismissIncomingHandoverRequest()}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
        >
          <motion.div
            className="flex w-full max-w-sm max-h-[85vh] [max-height:calc(100dvh-2rem)] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            {handoverRequestSuccess ? (
              <div className="flex flex-col items-center px-6 py-8 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
                  <svg className="h-7 w-7 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 id="incoming-handover-success" className="mt-4 text-lg font-bold text-slate-900">
                  Handover accepted
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  You can now deliver the medication from {handoverRequestSuccess}.
                </p>
                <button
                  type="button"
                  onClick={dismissIncomingHandoverRequest}
                  className="mt-6 w-full rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between border-b border-slate-200 px-5 py-3">
                  <div className="min-w-0">
                    <h3 id="incoming-handover-title" className="text-base font-bold text-slate-900">Handover request</h3>
                    <p className="mt-1 text-sm text-slate-600">From <span className="font-semibold text-slate-900">{incomingHandoverRequest.requestedByName}</span></p>
                  </div>
                  <button
                    type="button"
                    onClick={dismissIncomingHandoverRequest}
                    disabled={handoverRequestActionLoading}
                    className="ml-3 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                    aria-label="Close handover request"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m6 6 12 12M18 6 6 18" /></svg>
                  </button>
                </div>
                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-3">
                  {incomingHandoverRequest.patients.length > 0 && (
                    <div className="max-h-32 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Medication</p>
                      <p className="mt-1 whitespace-pre-line text-sm leading-5 text-slate-700">{incomingHandoverRequest.patients}</p>
                    </div>
                  )}
                  {handoverRequestError && (
                    <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{handoverRequestError}</p>
                  )}
                  <div>
                    <HandoverSignaturePad
                      title="Signature"
                      description="Required to accept this handover."
                      value={receiverSignature}
                      onChange={(nextValue) => {
                        setReceiverSignature(nextValue);
                        if (handoverRequestError === "Signature is required.") {
                          setHandoverRequestError(null);
                        }
                      }}
                      className="border-slate-200 bg-white"
                      compact
                      canvasStyle={{ height: '128px' }}
                    />
                  </div>
                </div>
                <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-3">
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={handoverRequestActionLoading}
                      onClick={handleRejectHandoverRequest}
                      className="flex-1 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
                      style={{ minHeight: '44px' }}
                    >
                      Decline
                    </button>
                    <button
                      type="button"
                      disabled={handoverRequestActionLoading}
                      onClick={() => handleAcceptHandoverRequest(receiverSignature)}
                      className="flex-1 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-70"
                      style={{ minHeight: '44px' }}
                    >
                      {handoverRequestActionLoading ? "Please wait…" : "Accept handover"}
                    </button>
                  </div>
                  {/* <button
                    type="button"
                    disabled={handoverRequestActionLoading}
                    onClick={dismissIncomingHandoverRequest}
                    className="w-full rounded-xl border border-slate-500 px-4 py-3 text-sm font-medium text-slate-300 hover:bg-slate-700/50 disabled:opacity-70"
                  >
                    Review later in Route Control
                  </button> */}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Logout blocked — same style as handover notifications */}
      {logoutBlockedMessage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="logout-blocked-title"
          onClick={() => dispatch(clearLogoutBlockedMessage())}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-slate-800 px-6 py-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-900/60">
                <svg className="h-9 w-9 text-amber-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 id="logout-blocked-title" className="mt-5 text-lg font-semibold text-white">
                Logout blocked
              </h3>
              <p className="mt-2 text-base font-medium leading-snug text-slate-200">
                {logoutBlockedMessage}
              </p>
              {showLogoutBlockedHandoverButton ? (
                <button
                  type="button"
                  onClick={openRouteControlForLogoutBlocked}
                  className="mt-3 w-full rounded-xl bg-amber-500 px-4 py-3 text-sm font-semibold text-white hover:bg-amber-400"
                >
                  Handover now
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => dispatch(clearLogoutBlockedMessage())}
                className={`${showLogoutBlockedHandoverButton ? "mt-3" : "mt-6"} w-full rounded-xl bg-slate-600 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-500`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient spotted notification — in-app modal (no browser alert), with resolved address */}
        {patientSpottedNotification && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="patient-spotted-title"
          onClick={dismissPatientSpottedNotification}
        >
          <div
            className="flex w-full max-w-sm max-h-[85vh] flex-col rounded-2xl bg-slate-800 shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
              <h3 id="patient-spotted-title" className="text-lg font-semibold text-white">
                Patient spotted notification
              </h3>
              <p className="mt-2 text-sm text-slate-300">
                <span className="font-medium text-slate-400">Patient:</span>{" "}
                <span className="text-white">{patientSpottedNotification.patientName}</span>
              </p>
              {patientSpottedNotification.route ? (
                <p className="mt-1 text-sm text-slate-300">
                  <span className="font-medium text-slate-400">Route:</span>{" "}
                  <span className="text-white">{patientSpottedNotification.route}</span>
                </p>
              ) : null}
              <p className="mt-1 text-sm text-slate-300">
                <span className="font-medium text-slate-400">Seen by:</span>{" "}
                <span className="text-white">{patientSpottedNotification.seenBy}</span>
              </p>
              <div className="mt-2 rounded-xl bg-slate-700/50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Location</p>
                <p className="mt-0.5 text-xs text-slate-300">
                  {patientSpottedNotification.locationAddress === null &&
                  patientSpottedNotification.latitude != null &&
                  patientSpottedNotification.longitude != null
                    ? "Resolving address…"
                    : patientSpottedNotification.locationAddress ||
                      (patientSpottedNotification.latitude != null && patientSpottedNotification.longitude != null
                        ? `${Number(patientSpottedNotification.latitude).toFixed(5)}, ${Number(patientSpottedNotification.longitude).toFixed(5)}`
                        : "—")}
                </p>
              </div>
            </div>
            <div className="shrink-0 px-6 pb-6 pt-2">
              <button
                type="button"
                onClick={dismissPatientSpottedNotification}
                className="w-full rounded-xl bg-slate-600 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-500"
              >
                OK
              </button>
            </div>
          </div>
        </div>
        )}

      </>
    );
  }

export default App;
